import { startseite } from "@progressive-development/politik-model";

import type { GebietsAuswahl } from "../../config/gebiet-auswahl.js";

/**
 * Der Ortsbezug der Startseite — welches Gebiet zeigt welcher Block.
 *
 * ZWEI EBENEN, MIT ABSICHT. Der Steckbrief und das Wahlergebnis gibt es
 * amtlich bis zum Stadtteil; Drucksachen und Termine gibt es sinnvoll erst je
 * Stadtbezirk (nur 381 von 3.164 Drucksachen nennen überhaupt einen Ort). Ein
 * gemerkter Stadtteil zeigt deshalb SEINE Zahlen, aber die Vorgänge SEINES
 * Bezirks — und die Auswahl-Zeile sagt das mit dem Bezirksnamen, statt es zu
 * verschweigen. Acht der neun Bezirke haben einen gleichnamigen Stadtteil; die
 * Ebene mitzuschreiben ist deshalb keine Höflichkeit, sondern nötig.
 *
 * Alles hier arbeitet mit exakten Werten aus den Daten — Slug und Ebene. Kein
 * Namensvergleich, keine Ableitung aus der Form eines Schlüssels.
 */

/**
 * Das Gebiet, auf das sich Steckbrief und Wahlergebnis beziehen.
 *
 * Ohne Merker ist es die Gesamtstadt — der Eintrag dafür steht im selben
 * Verzeichnis, es gibt keinen Sonderfall in der Anzeige.
 */
export function gebietFuerMerker(
  gebiete: startseite.GebietEintrag[],
  merker: GebietsAuswahl | undefined,
): startseite.GebietEintrag | undefined {
  if (!merker) return gebiete.find((g) => g.ebene === "gesamtstadt");
  return gebiete.find((g) => g.ebene === merker.ebene && g.slug === merker.slug);
}

/**
 * Welcher Aufmacher zu laden ist.
 *
 * Immer ein Bezirks-Slug oder „stadtweit"; den Bezirk trägt der Merker selbst
 * mit, auch wenn ein Stadtteil gemerkt ist.
 */
export function aufmacherSlug(merker: GebietsAuswahl | undefined): string {
  return merker?.bezirkSlug || startseite.AUFMACHER_STADTWEIT;
}

/**
 * Der Name des Bezirks, auf den sich die Aufmacher-Blöcke beziehen.
 *
 * Der BEZIRK, nicht das gemerkte Gebiet: Genau darauf bezieht sich die
 * Fünferliste — ein gemerktes Sülz zeigt die Vorgänge Lindenthals. Ohne
 * Merker leer; die Anzeige setzt dann die Stadt ein.
 *
 * Bloßer Name statt „im Stadtbezirk X": Überschriften bleiben so kurz genug.
 * Der Preis ist ein grammatischer Schnitzer bei der Innenstadt („in
 * Innenstadt"), bewusst in Kauf genommen — die Alternative wäre eine
 * kuratierte Artikel-Tabelle für neun Namen.
 */
export function bezirkName(
  gebiete: startseite.GebietEintrag[],
  merker: GebietsAuswahl | undefined,
): string {
  if (!merker) return "";
  const bezirk = gebiete.find((g) => g.ebene === "stadtbezirk" && g.slug === merker.bezirkSlug);
  // Unbekannter Slug wird sichtbar durchgereicht statt still geschluckt.
  return bezirk?.name ?? merker.bezirkSlug;
}

/**
 * Der gemerkte STADTTEIL — nur, wenn wirklich einer gemerkt ist.
 *
 * Gegenstück zu `bezirkName`: Die Aufmacher-Blöcke zeigen den Bezirk, weil
 * Drucksachen auf Veedel-Ebene zu dünn sind (401 von 3.364 nennen überhaupt
 * einen Stadtteil, im Mittel drei je Veedel, 16 Veedel gar keine). Der Block
 * verschweigt den Sprung aber nicht, sondern bietet den Weg zur engeren Sicht
 * an — diese Funktion liefert, was dafür nötig ist.
 *
 * Bewusst OHNE Anzahl: Die Zahl je Stadtteil steht nur im Listen-Artefakt
 * (~210 KiB), und das lädt die Startseite nicht. Ein Weg ohne Zahl ist
 * billiger als eine Zahl mit Ladezeit; käme die Zahl je Gebiet einmal aus der
 * Pipeline, gehörte sie hier hinein.
 *
 * Ist der Slug im Verzeichnis unbekannt, wird er als Name durchgereicht —
 * sichtbar falsch statt still verschwunden.
 */
export function gemerkterStadtteil(
  gebiete: startseite.GebietEintrag[],
  merker: GebietsAuswahl | undefined,
): { name: string; slug: string; bezirkSlug: string } | undefined {
  if (merker?.ebene !== "stadtteil" || !merker.slug) return undefined;
  const eintrag = gebiete.find((g) => g.ebene === "stadtteil" && g.slug === merker.slug);
  // Der Bezirk gehört dazu: Die Liste filtert Stadtteile INNERHALB eines
  // Bezirks, ein Stadtteil-Filter allein greift dort nicht.
  return {
    name: eintrag?.name ?? merker.slug,
    slug: merker.slug,
    bezirkSlug: merker.bezirkSlug,
  };
}
